import { useEffect, useRef } from 'react';
// Aliased away from the global `Map` constructor — without the rename
// the page's `new Map<string, T>()` lookups would resolve to this
// React component instead of the JS built-in.
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { MAP_DEFAULT_PROPS } from '@my/ui';
import type { Address } from '@my/directory-api';

const COORD_DEFAULT = { lat: 53.35, lng: -6.26 };

type LatLng = { lat: number; lng: number };

/**
 * Two-pin booking map. Auto-fits bounds when both ports are resolved;
 * single pin when only one is known; wider Ireland-centred regional
 * view when neither is available.
 *
 * Accepts pre-computed coordinates so callers can source them from
 * address records, route-catalogue port data, or a hardcoded lookup —
 * without coupling this component to any particular data shape.
 *
 * Each map needs a unique id so multiple maps on the same page don't
 * share state via `useMap`. Stable across re-renders via the ref.
 */
export function BookingMap({
  departure,
  arrival,
}: {
  departure: LatLng | null;
  arrival: LatLng | null;
}) {
  const mapIdRef = useRef(`booking-map-${Math.random().toString(36).slice(2, 10)}`);
  const map = useMap(mapIdRef.current);

  const depCoords = departure;
  const arrCoords = arrival;

  // Imperative marker tracking — tear down on every re-render to avoid
  // overlay leaks when the accordion is closed then reopened.
  const departureMarkerRef = useRef<google.maps.Marker | null>(null);
  const arrivalMarkerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    if (!map) return;

    departureMarkerRef.current?.setMap(null);
    departureMarkerRef.current = null;
    arrivalMarkerRef.current?.setMap(null);
    arrivalMarkerRef.current = null;

    if (depCoords && arrCoords) {
      departureMarkerRef.current = new google.maps.Marker({
        map,
        position: depCoords,
        label: { text: 'D', color: 'white', fontWeight: 'bold' },
      });
      arrivalMarkerRef.current = new google.maps.Marker({
        map,
        position: arrCoords,
        label: { text: 'A', color: 'white', fontWeight: 'bold' },
      });
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(depCoords);
      bounds.extend(arrCoords);
      map.fitBounds(bounds, 48);
    } else if (depCoords || arrCoords) {
      const c = (depCoords ?? arrCoords) as LatLng;
      const onlyDep = depCoords != null;
      const marker = new google.maps.Marker({
        map,
        position: c,
        label: { text: onlyDep ? 'D' : 'A', color: 'white', fontWeight: 'bold' },
      });
      if (onlyDep) departureMarkerRef.current = marker;
      else arrivalMarkerRef.current = marker;
      map.panTo(c);
      map.setZoom(8);
    } else {
      map.panTo(COORD_DEFAULT);
      map.setZoom(5);
    }

    return () => {
      departureMarkerRef.current?.setMap(null);
      departureMarkerRef.current = null;
      arrivalMarkerRef.current?.setMap(null);
      arrivalMarkerRef.current = null;
    };
  }, [map, depCoords?.lat, depCoords?.lng, arrCoords?.lat, arrCoords?.lng]);

  return (
    <div className="h-full min-h-[260px] w-full overflow-hidden rounded-md border border-slate-200 bg-white">
      <GoogleMap
        id={mapIdRef.current}
        defaultCenter={COORD_DEFAULT}
        defaultZoom={5}
        {...MAP_DEFAULT_PROPS}
      />
    </div>
  );
}

/**
 * Single-pin task location map. Uses the same imperative marker pattern
 * as BookingMap so we don't need mapId / cloud-style registration for
 * advanced markers — the Google Maps JS API loader is established at
 * the app root via <APIProvider>.
 */
export function TaskLocationMap({ selected }: { selected: Address | null }) {
  const mapIdRef = useRef(`task-location-map-${Math.random().toString(36).slice(2, 10)}`);
  const map = useMap(mapIdRef.current);

  const hasCoords =
    selected != null &&
    selected.latitude != null &&
    selected.longitude != null &&
    !Number.isNaN(selected.latitude) &&
    !Number.isNaN(selected.longitude);
  const center = hasCoords
    ? { lat: selected.latitude as number, lng: selected.longitude as number }
    : COORD_DEFAULT;

  const markerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    if (!map) return;

    // Clean up previous marker before dropping a new one — return
    // cleanup handles unmount too.
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    if (hasCoords) {
      map.panTo(center);
      map.setZoom(15);
      markerRef.current = new google.maps.Marker({ map, position: center });
    } else {
      map.panTo(COORD_DEFAULT);
      map.setZoom(5);
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, [map, hasCoords, center.lat, center.lng]);

  return (
    <div className="h-full min-h-[260px] w-full overflow-hidden rounded-md border border-slate-200 bg-white">
      <GoogleMap
        id={mapIdRef.current}
        defaultCenter={center}
        defaultZoom={hasCoords ? 15 : 5}
        {...MAP_DEFAULT_PROPS}
      />
    </div>
  );
}
